import React, { useState, useEffect } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import NoData from '../../../components/common/NoData';
import { getAssessmentResultsInClassroom, getSpecificAssessmentResultInClassroom, getAssessmentAnalytics } from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';

function ClassroomAssessmentResult({ decodedClassroomId, decodedAssessmentId }) {
    const { permissions } = usePermissions();
    // console.log('decodedClassroomId:', decodedClassroomId);
    // console.log('decodedAssessmentId:', decodedAssessmentId);

    // Main results state
    const [results, setResults] = useState([]);
    const [allResults, setAllResults] = useState([]);
    const [groupedResults, setGroupedResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Search and pagination state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Expandable rows state
    const [expandedRows, setExpandedRows] = useState({});

    // Details modal state
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [resultDetails, setResultDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Analytics state
    const [analyticsData, setAnalyticsData] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(true);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);

    // Fetch results on component mount
    useEffect(() => {
        if (decodedClassroomId && decodedAssessmentId) {
            fetchResults();
            fetchAnalyticsData();
        } else {
            toast.error('Classroom ID or Assessment ID is missing');
        }
    }, [decodedClassroomId, decodedAssessmentId, currentPage, itemsPerPage, searchTerm]);

    // Function to fetch assessment results
    const fetchResults = async () => {
        if (!decodedClassroomId || !decodedAssessmentId) {
            toast.error('Classroom ID or Assessment ID is missing');
            return;
        }

        setIsLoading(true);

        try {
            const payload = {
                class_id: decodedClassroomId,
                assessment_id: decodedAssessmentId,
                page: 1,
                limit: 100, // Fetch more results to ensure we get all attempts for each user
                search: searchTerm
            };

            // console.log('Fetching assessment results with payload:', payload);
            const response = await getAssessmentResultsInClassroom(payload);
            // console.log('Assessment results response:', response);

            if (response.success) {
                const allResultsData = response.data.results || [];
                setAllResults(allResultsData);

                // Group results by user_id
                const groupedByUser = {};

                allResultsData.forEach(result => {
                    if (!groupedByUser[result.user_id]) {
                        groupedByUser[result.user_id] = [];
                    }
                    groupedByUser[result.user_id].push(result);
                });

                // For each user, sort attempts by percentage (highest first) and take the best one
                const uniqueUserResults = Object.values(groupedByUser).map(userAttempts => {
                    // First sort by percentage (highest first)
                    const sortedByPercentage = userAttempts.sort((a, b) =>
                        parseFloat(b.percentage) - parseFloat(a.percentage)
                    );

                    // For the display in the main table, use the highest percentage attempt
                    const bestAttempt = sortedByPercentage[0];

                    // For the expanded view, sort by date (newest first)
                    const sortedByDate = [...userAttempts].sort((a, b) =>
                        new Date(b.attempt_date) - new Date(a.attempt_date)
                    );

                    // Return the best attempt along with all attempts for this user (sorted by date)
                    return {
                        ...bestAttempt,
                        allAttempts: sortedByDate
                    };
                });

                // Filter by search term if needed
                const filteredResults = searchTerm
                    ? uniqueUserResults.filter(result =>
                          result.username.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                    : uniqueUserResults;

                // Calculate pagination for the grouped results
                const totalItems = filteredResults.length;
                const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage);

                // Paginate the results
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedResults = filteredResults.slice(startIndex, endIndex);

                setGroupedResults(paginatedResults);
                setResults(paginatedResults);
                setTotalPages(calculatedTotalPages || 1);
                setTotalRecords(totalItems || 0);
            } else {
                toast.error(response.message || 'Failed to fetch assessment results');
                setResults([]);
                setGroupedResults([]);
            }
        } catch (error) {
            console.error('Error fetching results:', error);
            toast.error('An error occurred while fetching assessment results');
            setResults([]);
            setGroupedResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to fetch analytics data
    const fetchAnalyticsData = async () => {
        if (!decodedClassroomId || !decodedAssessmentId) {
            return;
        }

        if (!permissions.classroom_assessment_result_analytics) {
            setAnalyticsData(null);
            return;
        }

        setLoadingAnalytics(true);

        try {
            const payload = {
                class_id: decodedClassroomId,
                assessment_id: decodedAssessmentId
            };

            // console.log('Fetching analytics with payload:', payload);
            const response = await getAssessmentAnalytics(payload);
            // console.log('Analytics response:', response);

            if (response.success) {
                setAnalyticsData(response.data);
            } else {
                toast.error(response.message || 'Failed to load analytics data');
                setAnalyticsData(null);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Something went wrong while fetching analytics data');
            setAnalyticsData(null);
        } finally {
            setLoadingAnalytics(false);
        }
    };

    // Function to handle view result details
    const handleViewResult = async (result) => {
        setSelectedResult(result);
        setShowDetailsModal(true);
        setLoadingDetails(true);

        try {
            const payload = {
                results_id: result.id,
                user_id: result.user_id,
                class_id: decodedClassroomId,
                assessment_id: decodedAssessmentId
            };

            // console.log('Fetching result details with payload:', payload);
            const response = await getSpecificAssessmentResultInClassroom(payload);
            // console.log('Result details response:', response);

            if (response.success) {
                setResultDetails(response.data);
            } else {
                toast.error(response.message || 'Failed to load result details');
                setResultDetails(null);
            }
        } catch (error) {
            console.error('Error fetching result details:', error);
            toast.error('An error occurred while loading result details');
            setResultDetails(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Function to close details modal
    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedResult(null);
        setResultDetails(null);
    };

    // Function to toggle analytics visibility
    const handleToggleAnalytics = () => {
        setShowAnalytics(!showAnalytics);
    };

    // Function to toggle row expansion
    const toggleRowExpansion = (userId) => {
        setExpandedRows(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    return (
        <>
            {/* Analytics Toggle and Statistics Cards */}
            {showAnalytics && permissions.classroom_assessment_result_analytics && (
                <div className="mb-4">
                    <div className="w-full">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                        <Icon icon="fluent:chart-multiple-24-regular" className="text-primary" width="20" height="20" />
                                    </div>
                                    <h5 className="mb-0 text-lg font-semibold text-gray-900">Assessment Analytics</h5>
                                </div>
                                <Icon 
                                    icon="fluent:eye-off-24-regular" 
                                    width="20" 
                                    height="20" 
                                    className="text-gray-500 cursor-pointer"
                                    onClick={handleToggleAnalytics}
                                    style={{ cursor: 'pointer' }}
                                    title="Hide Analytics"
                                />
                            </div>
                            <div className="p-6">
                                {loadingAnalytics ? (
                                    <div className="text-center py-12">
                                        <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent"></div>
                                        <p className="mt-3 text-gray-600">Loading analytics...</p>
                                    </div>
                                ) : analyticsData ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                    <Icon icon="fluent:people-24-regular" className="text-primary" width="20" height="20" />
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium mb-1">Total Trainees</div>
                                            <div className="text-2xl font-bold text-gray-900">{analyticsData.total_trainees || 0}</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                                                    <Icon icon="fluent:checkmark-circle-24-regular" className="text-green-600" width="20" height="20" />
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium mb-1">Passed</div>
                                            <div className="text-2xl font-bold text-green-600">{analyticsData.passed || 0}</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                                                    <Icon icon="fluent:dismiss-circle-24-regular" className="text-red-600" width="20" height="20" />
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium mb-1">Failed</div>
                                            <div className="text-2xl font-bold text-red-600">{analyticsData.failed || 0}</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                                    <Icon icon="fluent:clock-24-regular" className="text-gray-600" width="20" height="20" />
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium mb-1">Not Attempted</div>
                                            <div className="text-2xl font-bold text-gray-900">{analyticsData.not_attempted || 0}</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                    <Icon icon="fluent:data-trending-24-regular" className="text-primary" width="20" height="20" />
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium mb-1">Pass Rate</div>
                                            <div className="text-2xl font-bold text-gray-900">
                                                {analyticsData.total_trainees > 0
                                                    ? Math.round((analyticsData.passed / analyticsData.total_trainees) * 100)
                                                    : 0
                                                }%
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                                <div
                                                    className={`h-1.5 rounded-full transition-all ${
                                                        analyticsData.total_trainees > 0
                                                            ? Math.round((analyticsData.passed / analyticsData.total_trainees) * 100) >= 70
                                                                ? 'bg-green-500'
                                                                : Math.round((analyticsData.passed / analyticsData.total_trainees) * 100) >= 40
                                                                    ? 'bg-primary'
                                                                    : 'bg-red-500'
                                                            : 'bg-gray-400'
                                                    }`}
                                                    style={{
                                                        width: `${analyticsData.total_trainees > 0
                                                            ? Math.round((analyticsData.passed / analyticsData.total_trainees) * 100)
                                                            : 0
                                                        }%`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                                    <Icon icon="fluent:target-24-regular" className="text-gray-600" width="20" height="20" />
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 font-medium mb-1">Passing %</div>
                                            <div className="text-2xl font-bold text-gray-900">{analyticsData.passing_percentage || 0}%</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                                            <Icon icon="fluent:chart-multiple-24-regular" className="text-gray-400" width="32" height="32" />
                                        </div>
                                        <p className="text-gray-500">No analytics data available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Show Analytics Button when hidden */}
            {!showAnalytics && permissions.classroom_assessment_result_analytics && (
                <div className="mb-3">
                    <div className="w-full flex justify-end">
                        <Icon 
                            icon="fluent:chart-multiple-24-regular" 
                            width="24" 
                            height="24" 
                            className="text-primary cursor-pointer"
                            onClick={handleToggleAnalytics}
                            style={{ cursor: 'pointer' }}
                            title="Show Analytics"
                        />
                    </div>
                </div>
            )}

            {/* Search Section */}
            <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="w-full md:w-auto">
                    <input
                        type="text"
                        className="w-full md:w-[300px] px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                        placeholder="Search by trainee name"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    Total: {totalRecords}
                </div>
            </div>

            {/* Results Table */}
            <div className="mb-4">
                <div className="w-full">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse mb-0">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">SL No</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-40">Username</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Total QA</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Attempted</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Correct</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Highest %</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Pass %</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Status</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5">
                                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                                <p className="mt-2">Loading results...</p>
                                            </td>
                                        </tr>
                                    ) : results.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5">
                                                <NoData caption="No assessment results available" />
                                            </td>
                                        </tr>
                                    ) : (
                                        results.map((result, index) => (
                                            <React.Fragment key={result.user_id || index}>
                                                {/* Main row showing the best attempt */}
                                                <tr
                                                    className="border-bottom"
                                                    style={{
                                                        cursor: result.allAttempts && result.allAttempts.length > 1 ? 'pointer' : 'default',
                                                        backgroundColor: expandedRows[result.user_id] ? '#f8f9fa' : 'transparent'
                                                    }}
                                                    onClick={() => {
                                                        if (result.allAttempts && result.allAttempts.length > 1) {
                                                            toggleRowExpansion(result.user_id);
                                                        }
                                                    }}
                                                >
                                                    <td className="px-4 py-3 align-middle">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                    <td className="px-4 py-3 align-middle">
                                                        <div className="flex items-center">
                                                            {result.username}
                                                            {result.allAttempts && result.allAttempts.length > 1 && (
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 ml-2" style={{ fontSize: '0.7rem' }}>
                                                                    {result.allAttempts.length} attempts
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 align-middle text-center">{result.total_questions}</td>
                                                    <td className="px-4 py-3 align-middle text-center">{result.questions_attempted}</td>
                                                    <td className="px-4 py-3 align-middle text-center">{result.correct_questions}</td>
                                                    <td className="px-4 py-3 align-middle text-center">{parseFloat(result.percentage) || 0}%</td>
                                                    <td className="px-4 py-3 align-middle text-center">{result.passing_percentage}%</td>
                                                    <td className="px-4 py-3 align-middle text-center">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${parseFloat(result.percentage) >= result.passing_percentage ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                            {parseFloat(result.percentage) >= result.passing_percentage ? 'Pass' : 'Fail'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 align-middle text-center">
                                                        <div className="flex justify-center gap-2">
                                                            <button
                                                                className="border border-gray-800 text-gray-700 hover:bg-gray-100 font-medium py-1 px-2 rounded-lg text-sm"
                                                                style={{ width: '36px', height: '36px' }}
                                                                title="View Details"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleViewResult(result);
                                                                }}
                                                            >
                                                                <Icon icon="fluent:eye-24-regular" width="16" height="16" />
                                                            </button>
                                                            {result.allAttempts && result.allAttempts.length > 1 && (
                                                                <button
                                                                    className="border border-gray-800 text-gray-700 hover:bg-gray-100 font-medium py-1 px-2 rounded-lg text-sm"
                                                                    style={{ width: '36px', height: '36px' }}
                                                                    title={expandedRows[result.user_id] ? "Hide Attempts" : "Show All Attempts"}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleRowExpansion(result.user_id);
                                                                    }}
                                                                >
                                                                    <Icon
                                                                        icon={expandedRows[result.user_id] ? "fluent:chevron-up-24-regular" : "fluent:chevron-down-24-regular"}
                                                                        width="16"
                                                                        height="16"
                                                                    />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* Expandable row for previous attempts */}
                                                {result.allAttempts && result.allAttempts.length > 1 && expandedRows[result.user_id] && (
                                                    <tr>
                                                        <td colSpan="9" className="p-0">
                                                            <div className="p-3 bg-gray-100">
                                                                <h6 className="mb-3">All Attempts for {result.username}</h6>
                                                                <div className="overflow-x-auto">
                                                                    <table className="w-full text-sm border border-gray-300 mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th style={{ width: '10%' }}>Attempt #</th>
                                                                                <th style={{ width: '20%' }}>Date</th>
                                                                                <th style={{ width: '10%' }}>Total QA</th>
                                                                                <th style={{ width: '10%' }}>Attempted</th>
                                                                                <th style={{ width: '10%' }}>Correct</th>
                                                                                <th style={{ width: '10%' }}>Percentage</th>
                                                                                <th style={{ width: '10%' }}>Status</th>
                                                                                <th style={{ width: '10%' }}>Action</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {result.allAttempts.map((attempt, attemptIndex) => (
                                                                                <tr key={attempt.id || `${result.user_id}-${attemptIndex}`}>
                                                                                    <td>{attemptIndex + 1}</td>
                                                                                    <td>{new Date(attempt.attempt_date).toLocaleString()}</td>
                                                                                    <td>{attempt.total_questions}</td>
                                                                                    <td>{attempt.questions_attempted}</td>
                                                                                    <td>{attempt.correct_questions}</td>
                                                                                    <td>{parseFloat(attempt.percentage) || 0}%</td>
                                                                                    <td>
                                                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${parseFloat(attempt.percentage) >= result.passing_percentage ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                                            {parseFloat(attempt.percentage) >= result.passing_percentage ? 'Pass' : 'Fail'}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>
                                                                                        <button
                                                                                            className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium py-1 px-2 rounded-lg text-sm"
                                                                                            style={{ width: '32px', height: '32px' }}
                                                                                            title="View Details"
                                                                                            onClick={() => handleViewResult(attempt)}
                                                                                        >
                                                                                            <Icon icon="fluent:eye-24-regular" width="14" height="14" />
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Record per page and pagination  */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3 text-sm">
                    <select
                        className="px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                    >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isLoading}
                        aria-label="Previous page"
                    >
                        <Icon icon="fluent:chevron-left-24-regular" width="16" height="16" />
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages || isLoading}
                        aria-label="Next page"
                    >
                        <Icon icon="fluent:chevron-right-24-regular" width="16" height="16" />
                    </button>
                </div>
            </div>

            <style>
                {`
                    .wider-assessment-modal {
                        max-width: 95%;
                        width: 1400px;
                    }
                    @media (max-width: 768px) {
                        .wider-assessment-modal {
                            max-width: 100%;
                            width: 100%;
                        }
                    }
                `}
            </style>

            {/* Assessment Result Details Modal */}
            {showDetailsModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={handleCloseDetailsModal}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <Icon icon="fluent:document-data-24-regular" width="20" height="20" />
                                <h5 className="mb-0 font-semibold text-lg">Assessment Result Details</h5>
                            </div>
                            <button
                                onClick={handleCloseDetailsModal}
                                className="p-1 rounded hover:bg-gray-100"
                                aria-label="Close"
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="24" height="24" />
                            </button>
                        </div>
                        <div className="overflow-y-auto flex-1">
                            {loadingDetails ? (
                                <div className="text-center py-5">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    <p className="mt-2">Loading result details...</p>
                                </div>
                            ) : resultDetails ? (
                                <div className="result-details">
                                    {/* Summary Section */}
                                    <div className="p-4">
                                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-4">
                                            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                                <h5 className="mb-0 flex items-center">
                                                    <Icon icon="fluent:person-24-regular" className="mr-2" />
                                                    Result Summary
                                                </h5>
                                            </div>
                                            <div className="p-4">
                                                <div className="flex">
                                                    <div className="md:w-1/2">
                                                        <div className="flex justify-between mb-3">
                                                            <strong>Username:</strong>
                                                            <span>{selectedResult?.username}</span>
                                                        </div>
                                                        <div className="flex justify-between mb-3">
                                                            <strong>Total Questions:</strong>
                                                            <span>{resultDetails.summary?.total_questions}</span>
                                                        </div>
                                                        <div className="flex justify-between mb-3">
                                                            <strong>Attempted Questions:</strong>
                                                            <span>{resultDetails.summary?.total_attempted}</span>
                                                        </div>
                                                    </div>
                                                    <div className="md:w-1/2">
                                                        <div className="flex justify-between mb-3">
                                                            <strong>Correct Answers:</strong>
                                                            <span>{resultDetails.summary?.total_correct}</span>
                                                        </div>
                                                        <div className="flex justify-between mb-3">
                                                            <strong>Result Status:</strong>
                                                            <span className={`font-bold ${
                                                                resultDetails.summary?.result_status === 'Pass' ? 'text-green-500' : 'text-red-500'
                                                            }`}>
                                                                {resultDetails.summary?.result_status}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between mb-3">
                                                            <strong>Attempt Date:</strong>
                                                            <span>{new Date(resultDetails.summary?.attempt_date).toLocaleString()}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Progress Bar */}
                                                <div className="mt-3">
                                                    <div className="flex justify-between mb-2">
                                                        <span><strong>Score Progress</strong></span>
                                                        <span><strong>{resultDetails.summary?.percentage}%</strong></span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                                        <div
                                                            className={`h-3 rounded-full ${
                                                                parseFloat(resultDetails.summary?.percentage) >= 70
                                                                    ? 'bg-green-500'
                                                                    : parseFloat(resultDetails.summary?.percentage) >= 40
                                                                    ? 'bg-yellow-500'
                                                                    : 'bg-red-500'
                                                            }`}
                                                            style={{
                                                                width: `${parseFloat(resultDetails.summary?.percentage) || 0}%`,
                                                                height: '12px'
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Questions Section */}
                                    <div className="px-4 pb-4">
                                        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                                            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                                <h5 className="mb-0 flex items-center">
                                                    <Icon icon="fluent:question-circle-24-regular" className="mr-2" />
                                                    Questions & Answers
                                                </h5>
                                            </div>
                                            <div className="p-0">
                                                <div className="overflow-x-auto">
                                                    <table className="w-full mb-0 hover:bg-gray-50">
                                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ width: '8%' }}>SL No</th>
                                                                <th style={{ width: '40%' }}>Question</th>
                                                                <th style={{ width: '15%' }}>Status</th>
                                                                <th style={{ width: '18%' }}>Your Answer</th>
                                                                <th style={{ width: '19%' }}>Correct Answer</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {resultDetails.questions?.map((question, index) => (
                                                                <tr key={question.question_id}>
                                                                    <td className="py-3">{index + 1}</td>
                                                                    <td className="py-3">{question.question_name}</td>
                                                                    <td className="py-3">
                                                                        {question.is_attempted ? (
                                                                            question.is_correct ? (
                                                                                <div className="flex items-center">
                                                                                    <Icon icon="fluent:checkmark-circle-24-filled" className="text-green-500 mr-2" style={{ fontSize: '1.2rem' }} />
                                                                                    <span className="text-green-500">Correct</span>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="flex items-center">
                                                                                    <Icon icon="fluent:dismiss-circle-24-filled" className="text-red-500 mr-2" style={{ fontSize: '1.2rem' }} />
                                                                                    <span className="text-red-500">Incorrect</span>
                                                                                </div>
                                                                            )
                                                                        ) : (
                                                                            <div className="flex items-center">
                                                                                <Icon icon="fluent:subtract-circle-24-regular" className="text-gray-500 mr-2" style={{ fontSize: '1.2rem' }} />
                                                                                <span className="text-gray-500">Not Attempted</span>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3">
                                                                        {question.is_attempted ? (
                                                                            <span className={question.is_correct ? 'text-green-500' : 'text-red-500'}>
                                                                                {question.user_selected_option?.option_value || 'N/A'}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-gray-500">Not answered</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-3">
                                                                        <span className="text-green-500 font-bold">
                                                                            {question.correct_option?.option_value || 'N/A'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <Icon icon="fluent:warning-24-regular" style={{ fontSize: '3rem', color: '#f59e0b' }} />
                                    <p className="mt-2">No result details available</p>
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                            <button
                                className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                                onClick={handleCloseDetailsModal}
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="16" height="16" />
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ClassroomAssessmentResult

